import React from 'react';
import * as Icon from 'react-bootstrap-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { TFunction } from 'i18next';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import {
  GetOrderExecutionWithCurrencyDto,
  GetProjectDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import {
  CurrencyIsoTypeEnum,
  OrderExecutionStatusObj,
} from '../../../common/pokCore/validation/enums';
import momencik from '../../../common/momencik';
import mathUtils from '../../../utils/mathUtils';
import numberFormatter from '../../../common/numberFormatter';
import { teamName } from '../../../utils/teamName';

interface OrderExecutionSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  isProjectView?: boolean;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  emptyGridMessage?: string;
  refresh?: boolean;
  withAmount?: boolean;
}

export const orderExecutionForGrid = (
  t: TFunction<'orderLng', 'orderLng'>,
  tk: TKeys<'orderLng'>,
  orderExecution: GetOrderExecutionWithCurrencyDto,
  currentProjectId?: string,
) => {
  const buildProjectLabel = (project?: GetProjectDto) => {
    if (project) {
      return `${project.name} (${project.number})`;
    }

    return '-';
  };

  return {
    key: orderExecution.id,
    values: {
      'orderExecution.systemNumber': orderExecution.systemNumber,
      'employee.name': orderExecution?.orderExecutionEmployees
        ?.filter(({ active }) => active)
        .map(({ employee }) => employee?.name)
        .join(', '),
      'orderExecution.isResultProject':
        orderExecution.resultProject?.id === currentProjectId ? (
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip>{t(tk.orderExec.incomingOrder)}</Tooltip>}
          >
            <Icon.ArrowLeftSquareFill className="icon-red" />
          </OverlayTrigger>
        ) : (
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip>{t(tk.orderExec.outgoingOrder)}</Tooltip>}
          >
            <Icon.ArrowRightSquareFill className="icon-green" />
          </OverlayTrigger>
        ),
      result_project_label: buildProjectLabel(orderExecution.resultProject),
      project_label: buildProjectLabel(orderExecution.project),
      'orderExecution.rejectedNote': orderExecution.rejectedNote || '-',
      'orderExecution.status': t(
        OrderExecutionStatusObj[
          orderExecution.status as keyof typeof OrderExecutionStatusObj
        ],
      ),
      'creator.name': orderExecution.creator.name,
      'orderExecution.creationTs': momencik(orderExecution.creationTs),
      'orderExecution.date': momencik(orderExecution.date, 'YYYY-MM'),
      'team.name': orderExecution.project.projectTeams
        ?.map(({ team }) => teamName(team))
        .join(', '),
      'budget.name': orderExecution?.project?.purchaser?.budget?.name,
      amount: numberFormatter(
        orderExecution.orderExecutionPositions
          ?.map(inv => mathUtils.Big(inv.amount))
          .reduce((a, c) =>
            mathUtils.Big(mathUtils.add(c.toString(), a.toString())),
          )
          .toString() || 0,
        true,
        orderExecution.currencyCode as CurrencyIsoTypeEnum,
      ),
    },
    isInactive: !orderExecution.active,
    tdCssClass: {
      amount: 'amounts-align-right',
    },
  };
};

export const OrderExecutionSearch: React.FC<
  OrderExecutionSearchProps
> = props => {
  const { t, tk } = useTranslation('orderLng');
  const columns: GridColumn[] = [
    { header: t(tk.common.sysNumber), property: 'orderExecution.systemNumber' },
    { header: t(tk.prLang.project), property: 'project_label' },
    { header: t(tk.common.budget), property: 'budget.name' },
    ...(props.isProjectView
      ? [
          {
            header: t(tk.orderExec.realizationTeamUpperCase),
            property: 'team.name',
            noSorting: true,
          },
        ]
      : []),
    { header: t(tk.prLang.internalProject), property: 'result_project_label' },
    { header: t(tk.order.receivers), property: 'employee.name' },
    { header: t(tk.common.creator), property: 'creator.name' },
    {
      header: t(tk.common.creationDate),
      property: 'orderExecution.creationTs',
    },
    { header: t(tk.common.month), property: 'orderExecution.date' },
    ...(props.withAmount
      ? [
          {
            header: t(tk.orderExec.amount),
            property: 'amount',
            noSorting: true,
          },
        ]
      : []),
    {
      header: t(tk.common.status),
      property: 'orderExecution.status',
      noFilter: true,
    },
    {
      header: t(tk.order.rejectedNote),
      property: 'orderExecution.rejectedNote',
    },
    ...(props.isProjectView
      ? [
          {
            header: '',
            property: 'orderExecution.isResultProject',
            noSorting: true,
            noFilter: true,
          },
        ]
      : []),
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="orderExecution.systemNumber"
      initialOrderDirection="DESC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={props.emptyGridMessage || t(tk.orderExec.noOrderExec)}
      onRowClick={props.onRowClick}
      refresh={props.refresh}
      withFilterInColumns={true}
    />
  );
};
